<template>
  <!-- The public booking page a client sees (workspace-public-website PublicBookingExperience, step
       layout), on the feature stage backdrop: Select a service → Select a time → Your details →
       the payment summary → You're booked. Strings are the page's own (locales/booking/en.json);
       the business, people and prices come from data/booking-samples.ts. Two notes outside the
       window are the site's, not the page's: the Google event that blocks times (the client only
       sees fewer slots) and the intake questions. With `autoplay` it plays the steps once when in
       view (about 9 s) and rests on the last with Replay; otherwise `state` drives it (the sticky
       chapters). The server and reduce-motion render the resting frame. -->
  <div ref="rootEl" class="sbk" :class="[{ 'sbk--tall': tall }, `is-${current}`]" role="img" :aria-label="label">
    <div class="sbk-frame" aria-hidden="true">
      <div class="sbk-window">
        <div class="sbk-bar"><i /><i /><i /><span translate="no">{{ sample.path }}</span></div>
        <div class="sbk-page" translate="no">
          <div class="sbk-biz">
            <span class="sbk-logo">{{ sample.initials }}</span>
            <b>{{ sample.business }}</b>
          </div>
          <ol class="sbk-steps">
            <li v-for="(step, i) in stepper" :key="step" :class="{ 'is-on': i === stepIndex, 'is-done': i < stepIndex }">
              <span>{{ i < stepIndex ? "✓" : i + 1 }}</span>{{ step }}
            </li>
          </ol>

          <div class="sbk-views">
            <!-- Select a service -->
            <section class="sbk-view" :class="{ 'is-on': current === 'service' }">
              <div class="sbk-h">Select a service</div>
              <div v-for="(service, i) in sample.services" :key="service.name" class="sbk-row" :class="{ 'is-pick': i === sample.pick }">
                <span><b>{{ service.name }}</b><small>{{ service.minutes }} min · {{ service.price }}</small></span>
                <i class="sbk-chev" />
              </div>
            </section>

            <!-- Select a time: the Google event takes its times away -->
            <section class="sbk-view" :class="{ 'is-on': current === 'time' }">
              <div class="sbk-h">Select a time</div>
              <p class="sbk-sub">{{ service.name }} · {{ service.minutes }} min with {{ sample.host }}</p>
              <p class="sbk-day">{{ sample.date }}</p>
              <div class="sbk-slots">
                <span
                  v-for="(slot, i) in sample.slots"
                  :key="slot"
                  class="sbk-slot"
                  :class="{ 'is-busy': sample.busy.slots.includes(slot), 'is-pick': slot === sample.time }"
                  :style="{ '--i': i }"
                >{{ slot }}<small>{{ meridiem(slot) }}</small></span>
              </div>
            </section>

            <!-- Your details, with the service's intake questions -->
            <section class="sbk-view" :class="{ 'is-on': current === 'details' }">
              <div class="sbk-h">Your details</div>
              <div class="sbk-field"><label>Name</label><div>{{ sample.client.name }}</div></div>
              <div class="sbk-field"><label>Email</label><div>{{ sample.client.email }}</div></div>
              <div v-for="(question, i) in sample.intake" :key="question.label" class="sbk-field sbk-intake" :style="{ '--i': i }">
                <label>{{ question.label }} <em>*</em></label>
                <div :class="{ 'is-select': question.kind === 'select' }">{{ question.value }}</div>
              </div>
            </section>

            <!-- Summary with the deposit, then checkout -->
            <section class="sbk-view" :class="{ 'is-on': current === 'pay' }">
              <div class="sbk-h">Summary</div>
              <div class="sbk-sum">
                <div class="sbk-sum-line"><b>{{ service.name }}</b><span>{{ service.price }}</span></div>
                <p>{{ service.minutes }} min · with {{ sample.host }}</p>
                <p>{{ sample.date }} · {{ timeRange }}</p>
                <div class="sbk-sum-line sbk-sum-total"><span>Total</span><span>{{ sample.total }}</span></div>
                <div class="sbk-sum-line sbk-sum-due"><b>Due now</b><b>{{ sample.dueNow }}</b></div>
              </div>
              <p class="sbk-note">Payment is collected now and the appointment is confirmed after checkout.</p>
              <span class="sbk-btn">Pay &amp; Book Now</span>
            </section>

            <!-- Booked, with the after-payment note under it -->
            <section class="sbk-view" :class="{ 'is-on': current === 'booked' }">
              <div class="sbk-done">
                <span class="sbk-ok">✓</span>
                <div class="sbk-h">You're booked, {{ sample.client.first }}</div>
                <dl>
                  <div><dt>Service</dt><dd>{{ service.name }}</dd></div>
                  <div><dt>Time</dt><dd>{{ shortDate }} · {{ timeRange }}</dd></div>
                  <div><dt>Where</dt><dd>Video call</dd></div>
                </dl>
                <div class="sbk-acts"><span>Add to Google Calendar</span><span>Manage booking</span></div>
              </div>
              <div class="sbk-after">
                <small>A note from {{ sample.business }}</small>
                <p>{{ sample.afterPayment.note }}</p>
                <span class="sbk-btn sbk-btn--quiet">{{ sample.afterPayment.button }}</span>
              </div>
            </section>
          </div>
        </div>
      </div>

      <!-- Site notes (not part of the page) -->
      <div class="sbk-tag sbk-tag--gcal" :class="{ 'is-on': current === 'time' }">
        <span class="sbk-gdot" />
        <span><b>{{ sample.busy.title }}</b><small>Google Calendar · {{ sample.busy.time }}</small></span>
      </div>
      <div class="sbk-tag sbk-tag--intake" :class="{ 'is-on': current === 'details' }">
        <span><b>Your intake questions</b><small>Required before they can book</small></span>
      </div>
      <div class="sbk-tag sbk-tag--stripe" :class="{ 'is-on': current === 'pay' }">
        <span><b>Deposit through Stripe Checkout</b><small>Paid to your own Stripe account</small></span>
      </div>
    </div>
    <button v-show="canReplay" type="button" class="sbk-replay" aria-label="Replay preview" title="Replay preview" @click="play">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
    </button>
  </div>
</template>

<script setup>
import { bookingSamples } from "~/data/booking-samples";

const props = defineProps({
  sample: { type: String, default: "studio" },
  autoplay: { type: Boolean, default: false },
  state: { type: String, default: "booked" },
  tall: { type: Boolean, default: false },
  label: { type: String, default: "A sample booking page: the client picks a service and a time, answers the intake questions, pays a deposit and is booked." },
});

const order = ["service", "time", "details", "pay", "booked"];
// How long each screen holds while playing (ms); the whole run stays under 10 s.
const holds = { service: 1500, time: 2300, details: 1900, pay: 2100 };
const stepper = ["Service", "Availability", "Details"];

const sample = computed(() => bookingSamples[props.sample] || bookingSamples.studio);
const service = computed(() => sample.value.services[sample.value.pick]);
const meridiem = (slot) => (Number(slot.split(":")[0]) >= 9 && Number(slot.split(":")[0]) <= 11 ? "AM" : "PM");
const timeRange = computed(() => {
  const [h, m] = sample.value.time.split(":").map(Number);
  const end = h * 60 + m + service.value.minutes;
  const fmt = (t) => `${((Math.floor(t / 60) + 11) % 12) + 1}:${String(t % 60).padStart(2, "0")}`;
  const startTag = meridiem(sample.value.time);
  const endTag = Math.floor(end / 60) >= 12 ? "PM" : "AM";
  return `${fmt(h * 60 + m)} ${startTag} – ${fmt(end)} ${endTag}`;
});
const shortDate = computed(() => {
  const [day, num, month] = sample.value.date.split(" ");
  return `${day.slice(0, 3)} ${num} ${month.slice(0, 3)}`;
});

const rootEl = ref(null);
const playing = ref(null);
const resting = ref(false);
let timers = [];
let observer = null;

const current = computed(() => (props.autoplay ? playing.value ?? "booked" : props.state));
const stepIndex = computed(() => ({ service: 0, time: 1, details: 2, pay: 2, booked: 3 })[current.value] ?? 3);
const canReplay = computed(() => props.autoplay && resting.value);

const clear = () => {
  timers.forEach(clearTimeout);
  timers = [];
};
const play = () => {
  clear();
  resting.value = false;
  let at = 0;
  order.forEach((step) => {
    timers.push(setTimeout(() => { playing.value = step; }, at));
    at += holds[step] ?? 0;
  });
  timers.push(setTimeout(() => { resting.value = true; }, at + 600));
};

onMounted(() => {
  if (!props.autoplay) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    play();
  }, { threshold: 0.35 });
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  clear();
  observer?.disconnect();
});
</script>

<style>
.sbk {
  --sbk-p1: 233 107 76;
  --sbk-p2: 201 61 92;
  --sbk-dot: rgb(0 0 0 / 0.1);
  --sbk-surface: #fff;
  --sbk-sunken: #f6f6f4;
  --sbk-line: rgb(0 0 0 / 0.12);
  --sbk-text: #1c1c1c;
  --sbk-muted: #5c6068;
  --sbk-ink: #171717;
  --sbk-ink-text: #fff;
  --sbk-accent: #0e7490;
  --sbk-green: #2d7a4e;
  --sbk-green-fill: #e6f4ea;
  position: relative;
  height: 470px;
  border-radius: 14px;
  background-color: rgb(0 0 0 / 0.018);
  isolation: isolate;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
.dark .sbk {
  --sbk-dot: rgb(255 255 255 / 0.13);
  --sbk-surface: #1f1f1f;
  --sbk-sunken: #171717;
  --sbk-line: rgb(255 255 255 / 0.15);
  --sbk-text: #ececec;
  --sbk-muted: #a8a8a8;
  --sbk-ink: #ececec;
  --sbk-ink-text: #171717;
  --sbk-accent: #67e8f9;
  --sbk-green: #6ed39a;
  --sbk-green-fill: rgb(63 178 110 / 0.18);
  background-color: rgb(255 255 255 / 0.03);
}
.sbk--tall { height: 520px; }
@media (min-width: 1024px) { .sbk--tall .sbk-frame { max-width: 500px; } }
.sbk::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle, var(--sbk-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--sbk-p1) / 0.14) 0%, rgb(var(--sbk-p2) / 0.07) 50%, rgb(var(--sbk-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
.sbk-frame { position: relative; height: 100%; max-width: 440px; margin: 0 auto; padding: 26px 26px 30px; }
.sbk-window {
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--sbk-line);
  border-radius: 10px;
  background: var(--sbk-surface);
  box-shadow: 0 6px 22px rgb(0 0 0 / 0.08);
  color: var(--sbk-text);
  font-size: 12px;
  line-height: 1.4;
}
.sbk-bar { display: flex; align-items: center; gap: 5px; border-bottom: 1px solid var(--sbk-line); padding: 8px 10px; }
.sbk-bar i { width: 7px; height: 7px; border-radius: 50%; background: var(--sbk-line); }
.sbk-bar span { flex: 1; min-width: 0; margin-left: 6px; overflow: hidden; border-radius: 5px; background: var(--sbk-sunken); padding: 3px 8px; color: var(--sbk-muted); font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.sbk-page { padding: 14px 16px; }
.sbk-biz { display: flex; align-items: center; gap: 8px; }
.sbk-biz b { font-size: 13px; font-weight: 700; }
.sbk-logo { display: grid; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: var(--sbk-ink); color: var(--sbk-ink-text); font-size: 10px; font-weight: 800; }
.sbk-steps { display: flex; gap: 12px; margin: 12px 0 0; padding: 0 0 10px; border-bottom: 1px solid var(--sbk-line); list-style: none; color: var(--sbk-muted); font-size: 11px; }
.sbk-steps li { display: flex; align-items: center; gap: 5px; transition: color 0.3s ease; }
.sbk-steps li span { display: grid; place-items: center; width: 16px; height: 16px; border: 1px solid var(--sbk-line); border-radius: 50%; font-size: 9px; transition: background-color 0.3s ease, color 0.3s ease; }
.sbk-steps li.is-on { color: var(--sbk-text); font-weight: 600; }
.sbk-steps li.is-on span, .sbk-steps li.is-done span { border-color: var(--sbk-ink); background: var(--sbk-ink); color: var(--sbk-ink-text); }

.sbk-views { display: grid; margin-top: 12px; }
.sbk-view { grid-area: 1 / 1; opacity: 0; visibility: hidden; transform: translateY(6px); transition: opacity 0.35s ease, transform 0.35s ease, visibility 0s linear 0.35s; }
.sbk-view.is-on { opacity: 1; visibility: visible; transform: none; transition: opacity 0.35s ease 0.1s, transform 0.35s ease 0.1s, visibility 0s; }
.sbk-view .sbk-h { margin: 0 0 8px; font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.sbk-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 6px; border: 1px solid var(--sbk-line); border-radius: 8px; padding: 9px 11px; }
.sbk-row b { display: block; font-size: 12.5px; font-weight: 600; }
.sbk-row small { color: var(--sbk-muted); font-size: 11px; }
.sbk-row.is-pick { border-color: var(--sbk-ink); box-shadow: inset 0 0 0 1px var(--sbk-ink); }
.sbk-chev { width: 7px; height: 7px; border-top: 1.5px solid var(--sbk-muted); border-right: 1.5px solid var(--sbk-muted); transform: rotate(45deg); }
.sbk-sub, .sbk-day { margin: 0; color: var(--sbk-muted); font-size: 11px; }
.sbk-day { margin-top: 10px; color: var(--sbk-text); font-weight: 600; }
.sbk-slots { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin-top: 8px; }
.sbk-slot { border: 1px solid var(--sbk-line); border-radius: 6px; padding: 7px 0; text-align: center; font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }
.sbk-slot small { margin-left: 2px; color: var(--sbk-muted); font-size: 9px; font-weight: 500; }
.sbk-slot.is-pick { border-color: var(--sbk-ink); background: var(--sbk-ink); color: var(--sbk-ink-text); }
.sbk-slot.is-pick small { color: inherit; opacity: 0.75; }
/* Busy in Google: the client never sees these times. They rest gone (an empty dashed cell keeps
   the grid still), and fade out when the time step comes on. */
.sbk-slot.is-busy { border-style: dashed; color: transparent; }
.sbk-slot.is-busy small { color: transparent; }
.sbk-field { margin-top: 7px; }
.sbk-field label { display: block; color: var(--sbk-muted); font-size: 10.5px; font-weight: 600; }
.sbk-field label em { color: #ba3a37; font-style: normal; }
.sbk-field div { margin-top: 3px; border: 1px solid var(--sbk-line); border-radius: 6px; padding: 6px 9px; font-size: 12px; }
.sbk-field div.is-select { position: relative; padding-right: 24px; }
.sbk-field div.is-select::after { content: ""; position: absolute; top: 50%; right: 10px; width: 6px; height: 6px; margin-top: -5px; border-right: 1.5px solid var(--sbk-muted); border-bottom: 1.5px solid var(--sbk-muted); transform: rotate(45deg); }
.sbk-intake div { border-color: color-mix(in srgb, var(--sbk-accent) 55%, var(--sbk-line)); }
.sbk-sum { border: 1px solid var(--sbk-line); border-radius: 8px; padding: 10px 12px; }
.sbk-sum p { margin: 2px 0 0; color: var(--sbk-muted); font-size: 11px; }
.sbk-sum-line { display: flex; justify-content: space-between; gap: 8px; }
.sbk-sum-total { margin-top: 9px; border-top: 1px solid var(--sbk-line); padding-top: 8px; color: var(--sbk-muted); }
.sbk-sum-due { margin-top: 4px; font-size: 13px; }
.sbk-note { margin: 9px 0 0; color: var(--sbk-muted); font-size: 11px; }
.sbk-btn { display: block; margin-top: 10px; border-radius: 7px; background: var(--sbk-ink); padding: 9px; color: var(--sbk-ink-text); text-align: center; font-size: 12.5px; font-weight: 700; }
.sbk-btn--quiet { margin-top: 8px; border: 1px solid var(--sbk-line); background: transparent; color: var(--sbk-text); font-weight: 600; }
.sbk-done { text-align: center; }
.sbk-ok { display: inline-grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: var(--sbk-green-fill); color: var(--sbk-green); font-size: 15px; font-weight: 800; }
.sbk-done .sbk-h { margin: 6px 0 6px; }
.sbk-done dl { display: grid; gap: 3px; margin: 0; text-align: left; }
.sbk-done dl div { display: flex; justify-content: space-between; gap: 8px; border-bottom: 1px solid var(--sbk-line); padding: 3px 0; }
.sbk-done dt { color: var(--sbk-muted); font-size: 11px; }
.sbk-done dd { margin: 0; font-size: 11.5px; font-weight: 600; text-align: right; }
.sbk-acts { display: flex; justify-content: center; gap: 6px; margin-top: 8px; }
.sbk-acts span { border: 1px solid var(--sbk-line); border-radius: 6px; padding: 5px 8px; font-size: 10.5px; font-weight: 600; }
.sbk-after { margin-top: 8px; border-radius: 8px; background: var(--sbk-sunken); padding: 9px 11px; }
.sbk-after small { color: var(--sbk-muted); font-size: 10.5px; font-weight: 600; }
.sbk-after p { margin: 2px 0 0; font-size: 11.5px; }

/* Site notes beside the window. They rest hidden; each shows with its step. */
.sbk-tag {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 230px;
  border: 1px solid var(--sbk-line);
  border-radius: 8px;
  background: var(--sbk-surface);
  box-shadow: 0 10px 26px -10px rgb(15 23 42 / 0.35);
  padding: 8px 11px;
  color: var(--sbk-text);
  font-size: 11px;
  line-height: 1.35;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.sbk-tag.is-on { opacity: 1; transform: none; transition-delay: 0.45s; }
.sbk-tag b { display: block; font-size: 11.5px; }
.sbk-tag small { color: var(--sbk-muted); font-size: 10.5px; }
.sbk-tag--gcal { right: 0; bottom: 20%; }
.sbk-tag--intake { right: 0; bottom: 14%; }
.sbk-tag--stripe { left: 0; bottom: 6%; }
.sbk-gdot { flex: none; width: 4px; align-self: stretch; border-radius: 2px; background: #1a73e8; }

/* The time step: Google's busy event lands, then its times fade out. */
.sbk.is-time .sbk-slot.is-busy { animation: sbk-busy 0.5s ease 0.95s both; }
@keyframes sbk-busy {
  from { border-style: solid; color: var(--sbk-text); opacity: 1; }
  40% { opacity: 0.35; }
  to { border-style: dashed; color: transparent; opacity: 1; }
}
.sbk.is-details .sbk-intake { animation: sbk-rise 0.4s ease calc(0.35s + var(--i) * 0.18s) both; }
@keyframes sbk-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

.sbk-replay {
  position: absolute;
  right: 8px;
  bottom: 2px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #595f68;
  cursor: pointer;
}
.dark .sbk-replay { color: #a3a3a3; }
.sbk-replay:hover { background: rgb(0 0 0 / 0.08); }
.sbk-replay:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.sbk-replay svg { width: 20px; height: 20px; fill: currentColor; }

@media (max-width: 639px) {
  .sbk, .sbk--tall { height: 500px; }
  .sbk-frame { padding: 12px 10px 46px; }
  .sbk-page { padding: 12px; }
  .sbk-steps { gap: 8px; }
  .sbk-tag { max-width: 200px; }
  .sbk-tag--gcal { right: -4px; bottom: 24px; }
  .sbk-tag--intake { right: -4px; bottom: 24px; }
  .sbk-tag--stripe { left: -4px; bottom: 24px; }
}
@media (prefers-reduced-motion: reduce) {
  .sbk *, .sbk *::before { animation: none !important; transition: none !important; }
}
</style>
